import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, X, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import CustomCursor from '../components/CustomCursor';
import Seo from '../components/Seo';
import SiteNav from '../components/SiteNav';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import './CareersPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const slugify = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const CareersPage = () => {
  const { t, lang } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const CACHE_KEY = 'bcg_jobs_cache';

    // Hydrate instantly from the session cache (stale-while-revalidate):
    // on repeat visits / back-nav the list shows immediately, no spinner.
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : null;
      if (Array.isArray(parsed) && parsed.length) {
        setJobs(parsed);
        setLoading(false);
      }
    } catch { /* ignore bad cache */ }

    // Always revalidate in the background and refresh the cache.
    const fetchJobs = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/jobs`);
        const list = Array.isArray(data) ? data : [];
        setJobs(list);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(list)); } catch { /* quota */ }
      } catch {
        // Keep any cached data on a failed refresh rather than blanking it.
        setJobs(prev => (prev && prev.length ? prev : []));
      }
      setLoading(false);
    };
    fetchJobs();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (slug) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [slug]);

  const departments = ['All', ...new Set((jobs || []).map(j => j.department))];
  const filtered = filter === 'All' ? jobs : jobs.filter(j => j.department === filter);
  const deptCounts = departments.map(d => ({
    name: d,
    count: d === 'All' ? jobs.length : jobs.filter(j => j.department === d).length,
  }));

  const selectedJob = slug ? jobs.find(j => slugify(j.title) === slug || j.id === slug) : null;

  const n = jobs.length;
  const roleCountText = lang === 'fr'
    ? `${n} poste${n !== 1 ? 's' : ''} ouvert${n !== 1 ? 's' : ''} dans le groupe.`
    : `${n} open role${n !== 1 ? 's' : ''} across the group.`;

  const openRole = useCallback((job) => {
    navigate(`/careers/${slugify(job.title)}`, { state: { fromList: true } });
  }, [navigate]);

  const closeModal = useCallback(() => {
    if (location.state?.fromList) {
      navigate(-1);
    } else {
      navigate('/careers', { replace: true });
    }
  }, [navigate, location.state]);

  // Close modal on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && slug) closeModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slug, closeModal]);

  return (
    <div className="careers-page">
      <Seo titleKey="seo.careers.title" descKey="seo.careers.description" path="/careers" />
      <CustomCursor />

      {/* Nav */}
      <SiteNav />

      {/* Hero */}
      <section className="careers-hero" data-testid="careers-hero">
        <div className="careers-hero-bg"></div>
        <div className="careers-hero-content">
          <h1>{t('careers.heroTitle')}</h1>
        </div>
      </section>

      {/* Open positions */}
      <section className="careers-section" id="open-roles" data-testid="careers-content">
        <div className="careers-section-header">
          <h2>{t('careers.openPositions')}</h2>
          <p className="careers-section-sub">{roleCountText}</p>
        </div>

        {/* Department filter */}
        <div className="careers-filter" data-testid="careers-filter">
          {deptCounts.map(({ name, count }) => (
            <button
              key={name}
              className={`filter-chip ${filter === name ? 'active' : ''}`}
              onClick={() => setFilter(name)}
              data-testid={`filter-${name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {name === 'All' ? t('careers.all') : name}<span className="filter-count">{count}</span>
            </button>
          ))}
        </div>

        {/* Job grid */}
        {loading ? (
          <div className="jobs-grid" data-testid="careers-loading" aria-busy="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <div className="job-card job-card-skeleton" key={i} aria-hidden="true">
                <div className="job-card-info">
                  <div className="skeleton sk-meta" />
                  <div className="skeleton sk-meta sk-meta-2" />
                  <div className="skeleton sk-title" />
                </div>
                <div className="skeleton sk-cta" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="careers-empty" data-testid="careers-empty">
            <p>{t('careers.empty')}</p>
          </div>
        ) : (
          <div className="jobs-grid" data-testid="jobs-list">
            {filtered.map(job => (
              <div
                key={job.id}
                className="job-card"
                onClick={() => openRole(job)}
                data-testid={`job-card-${job.id}`}
              >
                <div className="job-card-info">
                  <div className="job-card-meta">{job.department}</div>
                  <div className="job-card-meta">{job.location} · {job.employment_type}</div>
                  <h3 className="job-card-title">{job.title}</h3>
                </div>
                <div className="job-card-cta">
                  {t('careers.viewRole')} <ArrowRight size={14} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Open application */}
        <div className="open-application" data-testid="open-application">
          <p>{t('careers.openAppText')}</p>
          <a
            href="mailto:careers@brandscapitalgroup.com?subject=Open Application"
            className="open-application-link"
          >
            {t('careers.openAppLink')} <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="careers-footer">
        <div className="footer-content">
          <div>Brands Capital Group</div>
          <LanguageToggle />
          <div>{t('footer.copyright')}</div>
        </div>
      </footer>

      {/* Role detail modal */}
      {selectedJob && (
        <div className="role-overlay" onClick={closeModal} data-testid="role-modal-overlay">
          <div className="role-modal" onClick={e => e.stopPropagation()} data-testid="role-modal">
            <button className="role-modal-close" onClick={closeModal} data-testid="role-modal-close">
              <X size={20} />
            </button>

            <div className="role-modal-banner" />

            <div className="role-modal-header">
              <div className="role-modal-header-top">
                <div className="role-modal-header-left">
                  <div className="role-modal-badges">
                    <span className="role-badge">{selectedJob.department}</span>
                  </div>
                  <h2 className="role-modal-title">{selectedJob.title}</h2>
                  <div className="role-modal-meta">{selectedJob.location} · {selectedJob.employment_type}</div>
                </div>
                {selectedJob.apply_url ? (
                  <a
                    href={selectedJob.apply_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="role-apply-btn"
                    data-testid="role-apply-btn"
                  >
                    {t('careers.apply')}
                  </a>
                ) : (
                  <a
                    href={`mailto:careers@brandscapitalgroup.com?subject=Application: ${encodeURIComponent(selectedJob.title)}`}
                    className="role-apply-btn"
                    data-testid="role-apply-btn"
                  >
                    {t('careers.apply')}
                  </a>
                )}
              </div>
            </div>

            <div className="role-modal-body">
              {selectedJob.description && (
                <p className="role-modal-intro">{selectedJob.description}</p>
              )}

              <div className="role-modal-columns">
                {selectedJob.responsibilities && (
                  <div className="role-modal-col">
                    <h4>{t('careers.whatYoullDo')}</h4>
                    <ul className="role-bullet-list">
                      {selectedJob.responsibilities.split('\n').filter(l => l.trim()).map((line, i) => (
                        <li key={i}>{line.trim()}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedJob.requirements && (
                  <div className="role-modal-col">
                    <h4>{t('careers.whatLookingFor')}</h4>
                    <ul className="role-bullet-list">
                      {selectedJob.requirements.split('\n').filter(l => l.trim()).map((line, i) => (
                        <li key={i}>{line.trim()}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Direct URL visit — show full page if job not loaded yet */}
      {slug && !selectedJob && !loading && (
        <div className="role-overlay" data-testid="role-not-found">
          <div className="role-modal">
            <button className="role-modal-close" onClick={() => navigate('/careers')} data-testid="role-modal-close">
              <X size={20} />
            </button>
            <div className="role-modal-body" style={{ textAlign: 'center', padding: '80px 40px' }}>
              <h3 style={{ marginBottom: '16px' }}>{t('careers.notFound')}</h3>
              <p style={{ opacity: 0.5 }}>{t('careers.notFoundText')}</p>
              <Link to="/careers" className="role-back-link" style={{ marginTop: '24px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeft size={14} /> {t('careers.backToRoles')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersPage;
